import type { Transaction } from '@atlaskit/editor-prosemirror/state';
import { PluginKey } from '@atlaskit/editor-prosemirror/state';
import { SafePlugin } from '@atlaskit/editor-common/safe-plugin';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { sendableSteps } from '@atlaskit/prosemirror-collab';

import type { EditorOfflineStoragePlugin } from '../editorOfflineStoragePluginType';
import type { StepsDatabase } from '../stepsDatabase';

export type EditorOfflineStoragePluginStatus = 'pending' | 'restoring' | 'ready' | 'tooManyRetries';

export type EditorOfflineStoragePluginState = {
	status: EditorOfflineStoragePluginStatus;
	retryCount: number;
};

export const editorOfflineStoragePluginKey = new PluginKey<EditorOfflineStoragePluginState>(
	'editorOfflineStoragePlugin',
);

export const createPlugin =
	(api: PublicPluginAPI<EditorOfflineStoragePlugin> | undefined, stepsDatabase: StepsDatabase) =>
	() => {
		let readyToClearSteps = false;
		return new SafePlugin<EditorOfflineStoragePluginState>({
			key: editorOfflineStoragePluginKey,
			state: {
				init() {
					return {
						status: 'pending',
						retryCount: 0,
					};
				},
				apply: (tr: Transaction, currentPluginState: EditorOfflineStoragePluginState) => {
					const meta = tr.getMeta(editorOfflineStoragePluginKey);
					if (meta) {
						return {
							...currentPluginState,
							...meta,
						};
					}
					return currentPluginState;
				},
			},
			view: (editorView: EditorView) => {
				const stepsAreCleared = () => (sendableSteps(editorView.state)?.steps?.length ?? 0) === 0;

				const unsubscribe = api?.connectivity?.sharedState.onChange(
					({ prevSharedState, nextSharedState }) => {
						const { status } = editorOfflineStoragePluginKey.getState(editorView.state) ?? {};
						if (status !== 'ready') {
							return;
						}
						if (prevSharedState.mode === 'offline' && nextSharedState.mode === 'online') {
							if (stepsAreCleared()) {
								void stepsDatabase.clear();
							} else {
								readyToClearSteps = true;
							}
						} else if (nextSharedState.mode === 'offline') {
							readyToClearSteps = false;
						}
					},
				);

				return {
					update() {
						// Wait until unconfirmed steps is zero and we have "cleared" any updates
						if (readyToClearSteps && stepsAreCleared()) {
							void stepsDatabase.clear();
							readyToClearSteps = false;
						}
					},
					destroy() {
						unsubscribe?.();
					},
				};
			},
		});
	};
